// src/components/GymPlanner.js
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import CalendarIcon from '../assets/Calendar Default.png';

import BlogIcon from '../assets/Blog Default.png';


// ---- date helpers (no libs)
const isoDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); 
  const daysToSubtract = dow === 0 ? 6 : dow - 1;
  
  x.setDate(x.getDate() - daysToSubtract);
  x.setHours(0, 0, 0, 0);
  return x;
};
const dateForDow = (weekStart, dowIndexMon0) => {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + dowIndexMon0);
  return d;
};

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function GymPlanner({ user }) {
  const [tab, setTab] = useState('thisWeek'); // 'thisWeek' | 'plan'
  const [selectedWeekAnchor, setSelectedWeekAnchor] = useState(new Date()); // any day in week
  const weekStart = useMemo(() => startOfWeek(selectedWeekAnchor), [selectedWeekAnchor]);

  // ---- workout editing state
  const [editingWorkoutId, setEditingWorkoutId] = useState(null);
  const [workoutEdit, setWorkoutEdit] = useState({ reps: '', weight: '', notes: '' });

  // ---- plan state (recurring)
  const [plan, setPlan] = useState({}); // { Monday: [rows], ... }
  const [planForm, setPlanForm] = useState({ day: 'Monday', exercise: '', reps: '', weight: '', notes: '' });
  const [planEditingId, setPlanEditingId] = useState(null);
  const [planEdit, setPlanEdit] = useState({ exercise: '', reps: '', weight: '', notes: '' });

  // ---- week checklist state (actual workouts table)
  const [itemsByDay, setItemsByDay] = useState({}); // { Monday: [rows], ... }
  const [err, setErr] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [loadingWeek, setLoadingWeek] = useState(true);
  
  // ---- adding individual workouts to This Week
  const [addingToDay, setAddingToDay] = useState(null); // which day is showing the add form
  const [newWorkoutForm, setNewWorkoutForm] = useState({ exercise: '', reps: '', weight: '', notes: '' });

  // ---- help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  // ---- workout selection and copy state
  const [selectedWorkouts, setSelectedWorkouts] = useState(new Set());
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargetDate, setCopyTargetDate] = useState(new Date());

  // ---- collapsible days state
  const [collapsedDays, setCollapsedDays] = useState({}); // { Monday: true, Tuesday: false, ... }

  const toggleDay = (day) => {
    setCollapsedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // Clear seeded weeks cache when user changes or component unmounts
  const seededWeeksRef = useRef(new Set());
  const seedingInProgressRef = useRef(new Set());

  useEffect(() => {
    seededWeeksRef.current.clear();
    seedingInProgressRef.current.clear();
  }, [user?.id]);

  // ====== LOAD WEEKLY PLAN ======
  const fetchPlan = useCallback(async () => {
    if (!user?.id) return;
    setLoadingPlan(true); 
    setErr(''); // Clear previous errors
    
    const { data, error } = await supabase
      .from('workout_plan')
      .select('*')
      .eq('user_id', user.id)
      .order('day', { ascending: true })
      .order('sort_order', { ascending: true });

    if (error) {
      console.error(error);
      setErr('Failed to load weekly plan.');
      setPlan({});
    } else {
      const byDay = {};
      DAYS.forEach(d => (byDay[d] = []));
      (data || []).forEach((r) => {
        // Prefer exact 'day' label stored in DB
        const key = r.day && DAYS.includes(r.day) ? r.day : 'Monday'; // sensible fallback
        byDay[key].push(r);
      });
      setPlan(byDay);
    }
    setLoadingPlan(false);
  }, [user?.id]);

  // ====== LOAD THIS WEEK (workouts) ======
  const fetchWeek = useCallback(async () => {
    if (!user?.id) return;
    setLoadingWeek(true); 
    setErr(''); // Clear previous errors

    const monday = weekStart;
    const sunday = new Date(weekStart); sunday.setDate(monday.getDate() + 6);

    const { data, error } = await supabase
      .from('workouts')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', isoDate(monday))
      .lte('date', isoDate(sunday))
      .order('date', { ascending: true })
      .order('id', { ascending: true });

    if (error) {
      console.error(error);
      setErr('Failed to load this weeks workouts.');
      setItemsByDay({});
    } else {
      const byDay = {};
      DAYS.forEach(d => byDay[d] = []);
      (data || []).forEach(r => {
        // Prefer the stored day label. Fallback only if missing.
        const key = r.day && DAYS.includes(r.day)
          ? r.day
          : DAYS[(new Date((r.date || '') + 'T00:00:00').getDay() + 6) % 7]; // Monday=0 fallback
        byDay[key].push(r);
      });
      setItemsByDay(byDay);
    }
    setLoadingWeek(false);
  }, [user?.id, weekStart]);

  // ====== SEED WEEK FROM PLAN (idempotent) ======
  const ensureWeekSeeded = useCallback(
    async ({ force = false } = {}) => {
      if (!user?.id) return;

      const weekKey = `${user.id}:${isoDate(weekStart)}`;
      
      // Check if seeding is already in progress for this week
      if (seedingInProgressRef.current.has(weekKey)) {
        console.log('Seeding already in progress for this week, skipping...');
        return;
      }

      if (!force && seededWeeksRef.current.has(weekKey)) return;

      // Mark seeding as in progress
      seedingInProgressRef.current.add(weekKey);

      try {
        // Always check database for existing workouts, even if not in local cache
        const monday = weekStart;
        const sunday = new Date(weekStart); sunday.setDate(monday.getDate() + 6);
        
        const { data: existingWorkouts, error: fetchError } = await supabase
          .from('workouts')
          .select('date, exercise, reps, weight, notes')
          .eq('user_id', user.id)
          .gte('date', isoDate(monday))
          .lte('date', isoDate(sunday));

        if (fetchError) {
          console.error('Error fetching existing workouts:', fetchError);
          setErr('Could not check existing workouts.');
          return;
        }

        // Create a more comprehensive check for existing workouts
        const existingByDate = {};
        (existingWorkouts || []).forEach(w => {
          if (!existingByDate[w.date]) existingByDate[w.date] = [];
          existingByDate[w.date].push(w);
        });

        const inserts = [];
        DAYS.forEach((day, idx) => {
          const date = isoDate(dateForDow(weekStart, idx));
          const planRows = plan[day] || [];
          const existingForDay = existingByDate[date] || [];

          // Only seed if there are NO existing workouts for this day and we have plan items
          if (existingForDay.length === 0 && planRows.length > 0) {
            planRows.forEach((tpl) => {
              inserts.push({
                user_id: user.id,
                date,
                day,
                exercise: tpl.exercise,
                reps: tpl.reps ?? null,
                weight: tpl.weight ?? null,
                notes: tpl.notes ?? '',
                completed: false,
                completed_date: null,
              });
            });
          }
        });

        if (inserts.length === 0) {
          // mark as seeded to prevent auto-retries in this session
          seededWeeksRef.current.add(weekKey);
          return;
        }

        setErr(''); // Clear previous errors before seeding
        
        // Insert all workouts at once
        const { error } = await supabase
          .from('workouts')
          .insert(inserts);
          
        if (error) {
          console.error('Seeding error:', error);
          // If we still get duplicates, it means there was a race condition
          // In this case, just mark as seeded and refresh
          if (error.code === '23505') {
            console.log('Race condition detected during seeding - this is expected when navigating quickly');
          } else {
            setErr('Could not generate this week from your plan.');
            return;
          }
        } else {
          console.log(`Seeding complete: ${inserts.length} workouts inserted`);
        }

        seededWeeksRef.current.add(weekKey);
        await fetchWeek(); // refresh checklist
        
      } finally {
        // Always remove the lock when done
        seedingInProgressRef.current.delete(weekKey);
      }
    },
    [user?.id, weekStart, plan, fetchWeek]
  );

  useEffect(() => { fetchPlan(); }, [fetchPlan]);
  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  // ====== PLAN CRUD ======
  const addPlanItem = async (e) => {
    e.preventDefault();
    setErr(''); // Clear previous errors
    const { day, exercise, reps, weight, notes } = planForm;
    if (!exercise.trim()) return;

    const sort_order = (plan[day]?.length || 0) + 1;
    const payload = {
      user_id: user.id,
      day,
      exercise: exercise.trim(),
      reps: reps ? parseInt(reps, 10) : null,
      weight: weight ? parseInt(weight, 10) : null,
      notes: notes?.trim() || '',
      sort_order
    };
    const { data, error } = await supabase.from('workout_plan').insert([payload]).select().single();
    if (error) { console.error(error); setErr('Failed to add to plan.'); return; }
    setPlan((prev) => ({ ...prev, [day]: [...(prev[day] || []), data] }));
    setPlanForm({ day, exercise: '', reps: '', weight: '', notes: '' });
  };

  const startEditPlan = (row) => {
    setPlanEditingId(row.id);
    setPlanEdit({
      exercise: row.exercise || '',
      reps: row.reps ?? '',
      weight: row.weight ?? '',
      notes: row.notes || ''
    });
  };

  const cancelEditPlan = () => {
    setPlanEditingId(null);
    setPlanEdit({ exercise: '', reps: '', weight: '', notes: '' });
  };

  const savePlanEdit = async (e, row) => {
    e.preventDefault();
    setErr(''); // Clear previous errors
    const update = {
      exercise: planEdit.exercise.trim(),
      reps: planEdit.reps ? parseInt(planEdit.reps, 10) : null,
      weight: planEdit.weight ? parseInt(planEdit.weight, 10) : null,
      notes: planEdit.notes?.trim() || ''
    };
    const { data, error } = await supabase.from('workout_plan').update(update).eq('id', row.id).select().single();
    if (error) { console.error(error); setErr('Failed to update plan.'); return; }
    setPlan((prev) => ({
      ...prev,
      [row.day]: (prev[row.day] || []).map(it => it.id === row.id ? data : it)
    }));
    cancelEditPlan();
  };

  const deletePlanItem = async (row) => {
    if (!window.confirm('Remove this from your plan?')) return;
    setErr(''); // Clear previous errors
    const { error } = await supabase.from('workout_plan').delete().eq('id', row.id);
    if (error) { console.error(error); setErr('Failed to delete from plan.'); return; }
    setPlan((prev) => ({
      ...prev,
      [row.day]: (prev[row.day] || []).filter(it => it.id !== row.id)
    }));
  };

  const startEditWorkout = (workout) => {
    setEditingWorkoutId(workout.id);
    setWorkoutEdit({
      reps: workout.reps ?? '',
      weight: workout.weight ?? '',
      notes: workout.notes || ''
    });
  };

  const cancelEditWorkout = () => {
    setEditingWorkoutId(null);
    setWorkoutEdit({ reps: '', weight: '', notes: '' });
  };

  const saveWorkoutEdit = async (e, workout) => {
    e.preventDefault();
    setErr(''); // Clear previous errors
    
    const update = {
      reps: workoutEdit.reps ? parseInt(workoutEdit.reps, 10) : null,
      weight: workoutEdit.weight ? parseInt(workoutEdit.weight, 10) : null,
      notes: workoutEdit.notes?.trim() || ''
    };

    // Update the workout
    const { data: updatedWorkout, error: workoutError } = await supabase
      .from('workouts')
      .update(update)
      .eq('id', workout.id)
      .select()
      .single();

    if (workoutError) {
      console.error('Workout update error:', workoutError);
      setErr('Failed to update workout.');
      return;
    }

    // Update the workout plan if this workout came from the plan
    // Find matching plan item by day and exercise
    const planItems = plan[workout.day] || [];
    const matchingPlanItem = planItems.find(p => p.exercise === workout.exercise);
    
    if (matchingPlanItem) {
      const planUpdate = {
        reps: update.reps,
        weight: update.weight,
        notes: update.notes
      };

      const { data: updatedPlan, error: planError } = await supabase
        .from('workout_plan')
        .update(planUpdate)
        .eq('id', matchingPlanItem.id)
        .select()
        .single();

      if (planError) {
        console.error('Plan update error:', planError);
        setErr('Workout updated but failed to update plan.');
      } else {
        // Update local plan state
        setPlan((prev) => ({
          ...prev,
          [workout.day]: prev[workout.day].map(item => 
            item.id === matchingPlanItem.id ? updatedPlan : item
          )
        }));
      }
    }

    // Update local workout state
    setItemsByDay((prev) => ({
      ...prev,
      [workout.day]: prev[workout.day].map(w => w.id === workout.id ? updatedWorkout : w)
    }));

    cancelEditWorkout();
  };

  // ====== WEEK CHECKLIST actions (workouts) ======
  const toggleCompleted = async (w) => {
    const next = !w.completed;
    const update = { completed: next, completed_date: next ? new Date().toISOString() : null };
    // optimistic
    setItemsByDay((prev) => ({
      ...prev,
      [w.day]: prev[w.day].map(x => x.id === w.id ? { ...x, ...update } : x)
    }));
    const { error } = await supabase.from('workouts').update(update).eq('id', w.id);
    if (error) {
      console.error(error);
      // rollback
      setItemsByDay((prev) => ({
        ...prev,
        [w.day]: prev[w.day].map(x => x.id === w.id ? { ...x, completed: w.completed, completed_date: w.completed_date } : x)
      }));
      setErr('Could not save your change. Try again.');
    }
  };

  const deleteWorkout = async (w) => {
    if (!window.confirm('Delete this item from this week?')) return;
    setErr(''); // Clear previous errors
    const snapshot = itemsByDay[w.day] || [];
    setItemsByDay((prev) => ({ ...prev, [w.day]: snapshot.filter(x => x.id !== w.id) }));
    const { error } = await supabase.from('workouts').delete().eq('id', w.id);
    if (error) {
      console.error(error);
      setItemsByDay((prev) => ({ ...prev, [w.day]: snapshot })); // rollback
      setErr('Failed to delete.');
    }
  };

  // ====== WORKOUT SELECTION & COPY FUNCTIONS ======
  const toggleWorkoutSelection = (workoutId) => {
    setSelectedWorkouts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workoutId)) {
        newSet.delete(workoutId);
      } else {
        newSet.add(workoutId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedWorkouts(new Set());
  };

  const handleCopyWorkouts = async () => {
    if (selectedWorkouts.size === 0 || !user?.id) return;

    try {
      const workoutsToCopy = [];
      Object.values(itemsByDay).forEach(dayList => {
        dayList.forEach(w => {
          if (selectedWorkouts.has(w.id)) {
            workoutsToCopy.push(w);
          }
        });
      });

      if (workoutsToCopy.length === 0) {
        setErr('No workouts selected to copy.');
        return;
      }

      const targetDate = isoDate(copyTargetDate);
      const targetDayIndex = (copyTargetDate.getDay() + 6) % 7;
      const targetDay = DAYS[targetDayIndex];

      const newWorkouts = workoutsToCopy.map(w => ({
        user_id: user.id,
        exercise: w.exercise,
        reps: w.reps ? parseInt(w.reps, 10) : null,
        weight: w.weight ? parseFloat(w.weight) : null,
        notes: w.notes || null,
        date: targetDate,
        day: targetDay,
        completed: false,
        completed_date: null
      }));

      const { error } = await supabase
        .from('workouts')
        .insert(newWorkouts)
        .select();

      if (error) {
        console.error('Error copying workouts:', error);
        setErr('Failed to copy workouts: ' + error.message);
      } else {
        setShowCopyModal(false);
        clearSelection();
        // Show success message
        setErr('');
        // Refresh if copying to current week
        const targetWeekStart = startOfWeek(copyTargetDate);
        if (isoDate(targetWeekStart) === isoDate(weekStart)) {
          await fetchWeek();
        }
      }
    } catch (error) {
      console.error('Error copying workouts:', error);
      setErr('Failed to copy workouts: ' + error.message);
    }
  };

  const today = new Date();
  const minCopyDate = new Date(today);
  minCopyDate.setDate(today.getDate() + 1);
  const maxCopyDate = new Date(today);
  maxCopyDate.setDate(today.getDate() + 56);

  // ====== ADD INDIVIDUAL WORKOUT TO THIS WEEK ======
  const addWorkoutToDay = async (e, day) => {
    e.preventDefault();
    setErr('');
    
    const { exercise, reps, weight, notes } = newWorkoutForm;
    if (!exercise.trim()) return;
    
    const dayIndex = DAYS.indexOf(day);
    const date = isoDate(dateForDow(weekStart, dayIndex));
    
    const payload = {
      user_id: user.id,
      date,
      day,
      exercise: exercise.trim(),
      reps: reps ? parseInt(reps, 10) : null,
      weight: weight ? parseInt(weight, 10) : null,
      notes: notes?.trim() || '',
      completed: false,
      completed_date: null
    };
    
    const { data, error } = await supabase.from('workouts').insert([payload]).select().single();
    if (error) {
      console.error(error);
      setErr('Failed to add workout.');
      return;
    }
    
    // Add to local state
    setItemsByDay((prev) => ({
      ...prev,
      [day]: [...(prev[day] || []), data]
    }));
    
    // Reset form and close
    setNewWorkoutForm({ exercise: '', reps: '', weight: '', notes: '' });
    setAddingToDay(null);
  };

  // ====== UI ======
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const weekLabel = `${isoDate(weekStart)} → ${isoDate(weekEnd)}`;
  
  // Check if the current week is empty
  const isWeekEmpty = DAYS.every(day => (itemsByDay[day] || []).length === 0);
  
  // Check if plan has any exercises
  const hasPlanItems = DAYS.some(day => (plan[day] || []).length > 0);
  
  // Manual seed handler
  const handleSeedWeek = async () => {
    await ensureWeekSeeded({ force: true });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2">
        <button
          className={`px-2 sm:px-4 py-2 rounded-lg border text-sm sm:text-base ${tab==='thisWeek'?'bg-purple-600/60 border-purple-400 text-white':'bg-white/10 border-white/20 text-purple-200'}`}
          onClick={() => setTab('thisWeek')}
        >
          This Week
        </button>
        <button
          className={`px-2 sm:px-4 py-2 rounded-lg border text-sm sm:text-base ${tab==='plan'?'bg-purple-600/60 border-purple-400 text-white':'bg-white/10 border-white/20 text-purple-200'}`}
          onClick={() => setTab('plan')}
        >
          Weekly Plan
        </button>
      </div>

      {tab === 'thisWeek' ? (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
              <img src={CalendarIcon} alt="Calendar" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" /> This Week
              <button
                onClick={() => setShowHelpModal(true)}
                className="ml-2 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center text-white text-xs sm:text-sm transition-colors"
                title="Help"
              >
                ?
              </button>
            </h2>
              {/* Copy button when workouts are selected */}
              {selectedWorkouts.size > 0 && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-purple-200 text-xs sm:text-sm">
                    {selectedWorkouts.size} selected
                  </span>
                  <button
                    onClick={() => setShowCopyModal(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition text-xs sm:text-sm flex items-center gap-1 sm:gap-2"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                    Copy
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-purple-300 hover:text-purple-100 text-xs sm:text-sm underline"
                  >
                    Clear
                  </button>
                </div>
              )}
              
              {/* Week navigation */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                className="px-2 sm:px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm sm:text-base"
                onClick={() => setSelectedWeekAnchor(d => { const x=new Date(d); x.setDate(x.getDate()-7); return x; })}
              >
                ← Prev
              </button>
              <div className="text-purple-200 text-xs sm:text-sm min-w-[180px] sm:min-w-[220px] text-center px-1">{weekLabel}</div>
              <button
                className="px-2 sm:px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm sm:text-base"
                onClick={() => setSelectedWeekAnchor(d => { const x=new Date(d); x.setDate(x.getDate()+7); return x; })}
              >
                Next →
              </button>
            </div>
          </div>

          {err && <div className="mb-4 rounded border border-red-400/40 bg-red-500/10 p-3 text-red-200 text-sm">{err}</div>}

          {/* Seed from plan button - shown when week is empty and plan exists */}
          {!loadingWeek && !loadingPlan && isWeekEmpty && hasPlanItems && (
            <div className="mb-4 p-4 rounded-xl border border-purple-400/30 bg-purple-500/10">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-purple-200 text-sm">
                  This week hasn't been planned yet. Copy exercises from your Weekly Plan to get started.
                </div>
                <button
                  onClick={handleSeedWeek}
                  className="whitespace-nowrap px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition text-sm flex items-center gap-2"
                >
                  <img src={BlogIcon} alt="Diet" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Copy from Weekly Plan
                </button>
              </div>
            </div>
          )}

          {/* Show message if week is empty but no plan exists */}
          {!loadingWeek && !loadingPlan && isWeekEmpty && !hasPlanItems && (
            <div className="mb-4 p-4 rounded-xl border border-purple-400/30 bg-purple-500/10">
              <div className="text-purple-200 text-sm text-center">
                No workouts planned yet. Create your{' '}
                <button 
                  onClick={() => setTab('plan')} 
                  className="text-purple-300 hover:text-purple-100 underline font-semibold"
                >
                  Weekly Plan
                </button>
                {' '}first to get started.
              </div>
            </div>
          )}

          {loadingWeek ? (
            <div className="text-purple-200">Loading week...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5">
              {DAYS.map((day, i) => {
                const list = itemsByDay[day] || [];
                const d = dateForDow(weekStart, i);
                const isCollapsed = collapsedDays[day];
                return (
                  <div key={day} className="rounded-xl border border-white/20 p-3 sm:p-4 bg-white/10">
                    <div 
                      className="flex items-center justify-between mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleDay(day)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">{isCollapsed ? '▶' : '▼'}</span>
                        <div className="text-white font-semibold text-sm sm:text-base">{day}</div>
                      </div>
                      <div className="text-xs text-purple-300">{isoDate(d)}</div>
                    </div>
                    {!isCollapsed && (
                      <>
                        <div className="space-y-2 sm:space-y-3 min-h-[80px] sm:min-h-[100px]">
                      {list.length === 0 ? (
                        <div className="text-purple-300 text-sm italic">No items yet</div>
                      ) : list.map(w => (
                        <div key={w.id} className="bg-white/10 border border-white/10 rounded-lg p-2 sm:p-3">
                          {editingWorkoutId === w.id ? (
                            <form onSubmit={(e) => saveWorkoutEdit(e, w)} className="space-y-2 sm:space-y-3">
                              <div className="flex gap-2 sm:gap-3 items-center">
                                {/* Selection checkbox */}
                                <button
                                  type="button"
                                  onClick={() => toggleWorkoutSelection(w.id)}
                                  className="flex-shrink-0"
                                >
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${
                                    selectedWorkouts.has(w.id)
                                      ? 'border-purple-400 bg-purple-400'
                                      : 'border-white/40 hover:border-purple-300'
                                  }`}>
                                    {selectedWorkouts.has(w.id) && (
                                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                        <path d="M5 13l4 4L19 7"></path>
                                      </svg>
                                    )}
                                  </div>
                                </button>
                                
                                <div className="flex-1">
                                  <div className="text-white font-medium mb-2 text-sm sm:text-base">{w.exercise}</div>
                                  <div className="grid grid-cols-2 gap-2 mb-2">
                                    <input
                                      type="number"
                                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                                      placeholder="Reps"
                                      value={workoutEdit.reps}
                                      onChange={(e) => setWorkoutEdit(s => ({...s, reps: e.target.value}))}
                                    />
                                    <input
                                      type="number"
                                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                                      placeholder="Weight (kg)"
                                      value={workoutEdit.weight}
                                      onChange={(e) => setWorkoutEdit(s => ({...s, weight: e.target.value}))}
                                    />
                                  </div>
                                  <input
                                    className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm mb-2"
                                    placeholder="Notes"
                                    value={workoutEdit.notes}
                                    onChange={(e) => setWorkoutEdit(s => ({...s, notes: e.target.value}))}
                                  />
                                  <div className="flex gap-2 text-xs">
                                    <button className="text-green-300 hover:text-green-200" type="submit">Save</button>
                                    <button className="text-gray-300 hover:text-gray-200" type="button" onClick={cancelEditWorkout}>Cancel</button>
                                  </div>
                                  {w.completed && w.completed_date && (
                                    <div className="text-green-300 mt-2 text-xs">✓ {new Date(w.completed_date).toLocaleDateString()}</div>
                                  )}
                                </div>

                                {/* Complete button */}
                                <button
                                  type="button"
                                  onClick={() => toggleCompleted(w)}
                                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                    w.completed
                                      ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                                      : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                                  }`}
                                >
                                  {w.completed ? 'Done' : 'Complete'}
                                </button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex gap-2 sm:gap-3 items-center">
                              {/* Selection checkbox */}
                              <button
                                onClick={() => toggleWorkoutSelection(w.id)}
                                className="flex-shrink-0"
                              >
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${
                                  selectedWorkouts.has(w.id)
                                    ? 'border-purple-400 bg-purple-400'
                                    : 'border-white/40 hover:border-purple-300'
                                }`}>
                                  {selectedWorkouts.has(w.id) && (
                                    <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                      <path d="M5 13l4 4L19 7"></path>
                                    </svg>
                                  )}
                                </div>
                              </button>
                              
                              <div className="flex-1 min-w-0">
                                <div className="text-white font-medium text-sm sm:text-base">{w.exercise}</div>
                                <div className="text-xs text-purple-200">
                                  {w.reps ? `${w.reps} reps` : ''}{w.reps && w.weight ? ' · ' : ''}{w.weight ? `${w.weight} kg` : ''}{(w.reps||w.weight) && w.notes ? ' · ' : ''}{w.notes || ''}
                                  {w.completed && w.completed_date && <div className="text-green-300 mt-1">✓ {new Date(w.completed_date).toLocaleDateString()}</div>}
                                </div>
                                <div className="mt-2 text-xs flex gap-3">
                                  <button className="text-blue-300 hover:text-blue-200" onClick={() => startEditWorkout(w)}>Edit</button>
                                  <button className="text-red-300 hover:text-red-200" onClick={() => deleteWorkout(w)}>Delete</button>
                                </div>
                              </div>

                              {/* Complete button */}
                              <button
                                onClick={() => toggleCompleted(w)}
                                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  w.completed
                                    ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                                    : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                                }`}
                              >
                                {w.completed ? 'Done' : 'Complete'}
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Add Exercise Button/Form */}
                    {addingToDay === day ? (
                      <form onSubmit={(e) => addWorkoutToDay(e, day)} className="mt-3 space-y-2">
                        <input
                          className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                          placeholder="Exercise name"
                          value={newWorkoutForm.exercise}
                          onChange={(e) => setNewWorkoutForm(f => ({ ...f, exercise: e.target.value }))}
                          required
                          autoFocus
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                            placeholder="Reps"
                            value={newWorkoutForm.reps}
                            onChange={(e) => setNewWorkoutForm(f => ({ ...f, reps: e.target.value }))}
                          />
                          <input
                            type="number"
                            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                            placeholder="Weight (kg)"
                            value={newWorkoutForm.weight}
                            onChange={(e) => setNewWorkoutForm(f => ({ ...f, weight: e.target.value }))}
                          />
                        </div>
                        <input
                          className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs sm:text-sm"
                          placeholder="Notes"
                          value={newWorkoutForm.notes}
                          onChange={(e) => setNewWorkoutForm(f => ({ ...f, notes: e.target.value }))}
                        />
                        <div className="flex gap-2 text-xs">
                          <button 
                            type="submit" 
                            className="text-green-300 hover:text-green-200 font-semibold"
                          >
                            Add
                          </button>
                          <button 
                            type="button" 
                            className="text-gray-300 hover:text-gray-200"
                            onClick={() => {
                              setAddingToDay(null);
                              setNewWorkoutForm({ exercise: '', reps: '', weight: '', notes: '' });
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setAddingToDay(day)}
                        className="mt-3 w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-purple-300 hover:text-purple-200 hover:bg-white/20 transition text-xs sm:text-sm font-semibold"
                      >
                        + Add Exercise
                      </button>
                    )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
              <img src={CalendarIcon} alt="Calendar" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" /> Weekly Plan
            </h2>
          </div>

          {err && <div className="mb-4 rounded border border-red-400/40 bg-red-500/10 p-3 text-red-200 text-sm">{err}</div>}

          {/* Add to plan */}
          <div className="mb-6 space-y-3">
            <form onSubmit={addPlanItem}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                <select
                  className="lg:col-span-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm sm:text-base [&>option]:bg-gray-800 [&>option]:text-white"
                  value={planForm.day}
                  onChange={(e) => setPlanForm(f => ({ ...f, day: e.target.value }))}
                >
                  {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <input
                  className="sm:col-span-2 lg:col-span-4 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm sm:text-base"
                  placeholder="Exercise"
                  value={planForm.exercise}
                  onChange={(e) => setPlanForm(f => ({ ...f, exercise: e.target.value }))}
                  required
                />
                <input
                  type="number"
                  className="lg:col-span-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm sm:text-base"
                  placeholder="Reps"
                  value={planForm.reps}
                  onChange={(e) => setPlanForm(f => ({ ...f, reps: e.target.value }))}
                />
                <input
                  type="number"
                  className="lg:col-span-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm sm:text-base"
                  placeholder="Weight (kg)"
                  value={planForm.weight}
                  onChange={(e) => setPlanForm(f => ({ ...f, weight: e.target.value }))}
                />
                <input
                  className="sm:col-span-2 lg:col-span-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm sm:text-base"
                  placeholder="Notes"
                  value={planForm.notes}
                  onChange={(e) => setPlanForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <button 
                type="submit" 
                className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 sm:px-5 py-2 sm:py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition text-sm sm:text-base mt-3"
              >
                Add to Plan
              </button>
            </form>
          </div>

          {loadingPlan ? (
            <div className="text-purple-200">Loading plan...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5">
              {DAYS.map(day => {
                const isCollapsed = collapsedDays[day];
                return (
                  <div key={day} className="rounded-xl border border-white/20 p-3 sm:p-4 bg-white/10">
                    <div 
                      className="flex items-center gap-2 mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleDay(day)}
                    >
                      <span className="text-white text-sm">{isCollapsed ? '▶' : '▼'}</span>
                      <div className="text-white font-semibold text-sm sm:text-base">{day}</div>
                    </div>
                    {!isCollapsed && (
                      <div className="space-y-2 sm:space-y-3">
                    {(plan[day] || []).length === 0 ? (
                      <div className="text-purple-300 text-sm italic">No items yet</div>
                    ) : (
                      (plan[day] || []).map(row => (
                        <div key={row.id} className="bg-white/10 border border-white/10 rounded-lg p-2 sm:p-3">
                          {planEditingId === row.id ? (
                            <form onSubmit={(e) => savePlanEdit(e, row)} className="space-y-2">
                              <input className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" value={planEdit.exercise} onChange={(e)=>setPlanEdit(s=>({...s,exercise:e.target.value}))} required />
                              <div className="grid grid-cols-2 gap-2">
                                <input type="number" className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" placeholder="Reps" value={planEdit.reps} onChange={(e)=>setPlanEdit(s=>({...s,reps:e.target.value}))} />
                                <input type="number" className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" placeholder="Weight" value={planEdit.weight} onChange={(e)=>setPlanEdit(s=>({...s,weight:e.target.value}))} />
                              </div>
                              <input className="w-full bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm" placeholder="Notes" value={planEdit.notes} onChange={(e)=>setPlanEdit(s=>({...s,notes:e.target.value}))} />
                              <div className="flex gap-3 text-xs mt-1">
                                <button className="text-green-300 hover:text-green-200" type="submit">Save</button>
                                <button className="text-gray-300 hover:text-gray-200" type="button" onClick={cancelEditPlan}>Cancel</button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex justify-between gap-2">
                              <div className="text-sm text-purple-200 flex-1">
                                <div className="text-white font-medium text-sm sm:text-base">{row.exercise}</div>
                                <div className="text-xs">
                                  {row.reps ? `${row.reps} reps` : ''}{row.reps && row.weight ? ' · ' : ''}{row.weight ? `${row.weight} kg` : ''}{(row.reps||row.weight) && row.notes ? ' · ' : ''}{row.notes || ''}
                                </div>
                              </div>
                              <div className="text-xs flex gap-2">
                                <button className="text-blue-300 hover:text-blue-200" onClick={() => startEditPlan(row)}>Edit</button>
                                <button className="text-red-300 hover:text-red-200" onClick={() => deletePlanItem(row)}>Delete</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Copy Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCopyModal(false)}>
          <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">Copy Workouts</h3>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-white/60 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <p className="text-purple-200 mb-4">
                  Copy {selectedWorkouts.size} selected workout{selectedWorkouts.size !== 1 ? 's' : ''} to:
                </p>
                
                <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                  <label className="block text-white font-semibold mb-3">
                    Select Date:
                  </label>
                  
                  <select
                    value={copyTargetDate.toISOString().split('T')[0]}
                    onChange={(e) => setCopyTargetDate(new Date(e.target.value + 'T00:00:00'))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-400"
                  >
                    {(() => {
                      const options = [];
                      const start = new Date(minCopyDate);
                      const end = new Date(maxCopyDate);
                      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const dateStr = new Date(d).toISOString().split('T')[0];
                        const displayStr = new Date(d).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric', 
                          year: 'numeric' 
                        });
                        options.push(
                          <option key={dateStr} value={dateStr}>
                            {displayStr}
                          </option>
                        );
                      }
                      return options;
                    })()}
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="flex-1 bg-white/10 border border-white/20 text-white px-4 py-3 rounded-lg font-semibold hover:bg-white/20 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCopyWorkouts}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition"
                >
                  Copy Workouts
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowHelpModal(false)}>
          <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-md rounded-2xl border border-white/20 p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">How to Use Gym Planner</h3>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-white/60 hover:text-white text-2xl leading-none"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6 text-white/90">
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Getting Started</h4>
                <p className="leading-relaxed">
                  The Gym Planner helps you organize and track your weekly workouts. Start by creating your Weekly Plan template, then use it to plan each week.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Step 1: Create Your Weekly Plan</h4>
                <p className="leading-relaxed mb-2">
                  Switch to the <span className="font-semibold text-purple-300">Weekly Plan</span> tab and add exercises for each day of the week. This creates your recurring workout template.
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                  <li>Select the day of the week</li>
                  <li>Enter the exercise name</li>
                  <li>Add target reps and weight (optional)</li>
                  <li>Include any notes or instructions</li>
                  <li>You can edit or delete plan exercises anytime</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Step 2: Plan Your Week</h4>
                <p className="leading-relaxed mb-2">
                  In the <span className="font-semibold text-purple-300">This Week</span> tab, you have two options to add exercises:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                  <li><span className="font-semibold">Copy from Weekly Plan:</span> Click the "📋 Copy from Weekly Plan" button to populate the entire week with your template</li>
                  <li><span className="font-semibold">Add Individual Exercises:</span> Use the "+ Add Exercise" button on any day to add one-off workouts</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Step 3: Track Your Progress</h4>
                <p className="leading-relaxed mb-2">
                  As you work out throughout the week:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                  <li>Check off exercises as you complete them</li>
                  <li>Edit reps, weight, or notes to match what you actually did</li>
                  <li>View completion timestamps to track when you worked out</li>
                  <li>Delete exercises you skip or don't want to track</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Week Navigation</h4>
                <p className="leading-relaxed mb-2">
                  Use the <span className="font-semibold text-purple-300">← Prev</span> and <span className="font-semibold text-purple-300">Next →</span> buttons to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                  <li>Review past weeks to track your progress</li>
                  <li>Plan future weeks in advance</li>
                  <li>Each week is independent - plan when you're ready</li>
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Tips for Success</h4>
                <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                  <li>Update your Weekly Plan as you progress or change your routine</li>
                  <li>Use the notes field to track how exercises felt or any adjustments</li>
                  <li>Mix planned exercises (from template) with one-off exercises (+ Add Exercise)</li>
                  <li>Editing exercises in This Week updates your actual performance, not your plan template</li>
                  <li>Check off workouts to see your completion rate and timestamps</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setShowHelpModal(false)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}


      <style jsx>{`
        select option {
          background: #1a1a2e;
          color: white;
        }
      `}</style>

    </div>
  );
}